
import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors
 * @property {HTMLTableRowElement[]} cartItemRows
 * @property {TextComponent} cartTotal
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) {
      return;
    }

    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });

    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return;

    const textComponent = lineItemRow.querySelector('text-component');
    textComponent?.shimmer?.();
  }

  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter(
        (row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key
      ),
    ];

    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;
    const template = document.getElementById('empty-cart-template');

    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(
        () => {
          this.replaceChildren(clone);
        },
        [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']
      );

      return;
    }

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) {
        return remove();
      }

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
      onAnimationEnd(row, remove);
    });
  }

  updateQuantity(config) {
    const marker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`
    );

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line,
      quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer?.();

    fetch(
      Theme.routes.cart_change_url,
      fetchConfig('json', { body })
    )
      .then((response) => response.text())
      .then(async (responseText) => {
        const updatedCart = JSON.parse(responseText);

        resetShimmer(this);

        if (updatedCart.errors) {
          this.#handleCartError(line, updatedCart);
          return;
        }

        const sectionHTML = updatedCart.sections?.[this.sectionId];

        if (!sectionHTML) {
          await sectionRenderer.renderSection(this.sectionId, {
            cache: false,
          });

          if (this.isDrawer) {
            this.#syncVisibleCartSummary(updatedCart);
          }

          return;
        }

        const newSectionDocument = new DOMParser().parseFromString(
          sectionHTML,
          'text/html'
        );

        const hiddenCount = newSectionDocument.querySelector(
          '[ref="cartItemCount"]'
        )?.textContent;

        const newCartItemCount = hiddenCount
          ? parseInt(hiddenCount, 10)
          : updatedCart.item_count ?? 0;

        this.#updateQuantitySelectors(updatedCart);

        this.dispatchEvent(
          new CartUpdateEvent(updatedCart, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: updatedCart.sections,
          })
        );

        await morphSection(
  this.sectionId,
  sectionHTML,
  this.isDrawer ? 'hydration' : 'full',
  { injectStylesheet: true }
);

        // GRETA BRAND:
        // Actualizar solamente el resumen visible.
        // No modifica líneas, cantidades ni descuentos.
        if (this.isDrawer) {
          this.#syncVisibleCartSummary(updatedCart);
        }

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error('Error al actualizar el carrito:', error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(marker);
      });
  }

  /**
   * GRETA BRAND
   *
   * Sincroniza el contador y el total utilizando los datos
   * calculados por Shopify en /cart/change.js.
   *
   * No modifica productos ni cantidades.
   */
  #syncVisibleCartSummary(updatedCart) {
    const drawer = this.closest('cart-drawer-component');

    if (!drawer) return;

    const itemCount = Number(updatedCart.item_count);
    const totalCents = Number(updatedCart.total_price);

    if (
      !Number.isFinite(itemCount) ||
      !Number.isFinite(totalCents)
    ) {
      return;
    }

    const formatMoney = (cents) => {
      const amount = cents / 100;

      return (
        new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(amount)
        + ' COP'
      );
    };

    const formattedTotal = formatMoney(totalCents);

    const updateSummary = () => {
      if (!drawer.isConnected) return;

      // Actualizar el total estimado del carrito lateral.
      const totalElement = drawer.querySelector(
        '[data-testid="cart-total-value"]'
      );

      if (totalElement) {
        const moneyElement = totalElement.querySelector('.money');

        if (moneyElement) {
          moneyElement.textContent = formattedTotal;
        } else {
          totalElement.textContent = formattedTotal;
        }

        totalElement.setAttribute('value', formattedTotal);
      }

      // Actualizar el contador del título "Carrito".
      const heading = drawer.querySelector('#cart-drawer-heading');

      if (heading) {
        const bubble = heading.querySelector('.cart-bubble');

        if (bubble) {
          const numberElement =
            bubble.querySelector('[data-testid="cart-count"]') ||
            bubble.querySelector('.cart-bubble__text') ||
            bubble.querySelector('[ref="cartBubbleCount"]') ||
            bubble.querySelector('span');

          if (numberElement) {
            numberElement.textContent = String(itemCount);
          } else {
            bubble.textContent = String(itemCount);
          }
        }
      }
    };

    // Ejecutar después del morph y de las actualizaciones
    // visuales pendientes del navegador.
    updateSummary();

    requestAnimationFrame(() => {
      updateSummary();
    });
  }

  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  #handleCartError = (line, updatedCart) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) {
      console.error('Quantity input not found');
      return;
    }

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer =
      this.refs[`cartItemErrorContainer-${line}`];

    if (
      !(cartItemError instanceof HTMLElement) ||
      !(cartItemErrorContainer instanceof HTMLElement)
    ) {
      console.error('Cart item error container not found');
      return;
    }

    cartItemError.textContent = updatedCart.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, {
        cache: false,
      });
      return;
    }

    if (event.target === this) {
      return;
    }

    const cartItemsHTML =
      event.detail.data.sections?.[this.sectionId];

    if (cartItemsHTML) {
      morphSection(
        this.sectionId,
        cartItemsHTML,
        'full',
        { injectStylesheet: true }
      );

      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, {
        cache: false,
      });
    }
  };

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();

      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector(
          'input[data-cart-quantity]'
        );

        if (!input) continue;

        input.setAttribute(
          'data-cart-quantity',
          item.quantity.toString()
        );

        if (
          'updateCartQuantity' in selector &&
          typeof selector.updateCartQuantity === 'function'
        ) {
          selector.updateCartQuantity();
        }
      }
    }
  }

  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll(
      'cart-quantity-selector-component'
    )) {
      selector.updateButtonStates?.();
    }
  }

  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) {
      throw new Error('Section id missing');
    }

    return sectionId;
  }

  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
